import React from "react";

/**
 * ErrorBoundary — catches render-time errors in its subtree and shows a
 * graceful fallback instead of a blank white page.
 *
 * Wrap the entire app at the root level, and optionally wrap high-risk
 * subtrees (charts, recharts ResponsiveContainer, third-party widgets).
 *
 * Usage:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 *
 * Production note: replace the console.error call with Sentry / LogRocket /
 * your error-tracking SDK before going live.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // TODO(prod): pipe to an error tracker.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ error: null, errorInfo: null });
    if (this.props.onReset) this.props.onReset();
  };

  render() {
    const { error, errorInfo } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return typeof this.props.fallback === "function"
        ? this.props.fallback({ error, errorInfo, reset: this.handleReset })
        : this.props.fallback;
    }

    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-8 text-center">
        <div className="max-w-md bg-white rounded-2xl shadow-lg p-8 border border-red-100">
          <div className="text-5xl mb-4">😵</div>
          <h1 className="text-xl font-bold mb-2 text-gray-800">
            Something went wrong
          </h1>
          <p className="text-sm text-gray-500 mb-4">
            {error.message || "An unexpected error occurred while rendering this page."}
          </p>
          {process.env.NODE_ENV !== "production" && errorInfo && (
            <details className="text-left text-xs text-gray-400 mb-4 overflow-auto max-h-40">
              <summary className="cursor-pointer">Stack trace</summary>
              <pre className="whitespace-pre-wrap">{errorInfo.componentStack}</pre>
            </details>
          )}
          <div className="flex gap-2 justify-center">
            <button
              onClick={this.handleReset}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Try again
            </button>
            <button
              onClick={() => (window.location.href = "/")}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
